import { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trackPageLoad } from '@fff-web/fff-analytics';
import Layout from '../../components/Layout';
import { getCarriers } from '../../services/carrier';
import { getOrders } from '../../services/orders';
import { getOrderTracking } from '../../services/tracking';
import PageContainer from '../MyAccount/shared-components/PageContainer/PageContainer';
import { getTrackingUrl } from '../MyAccount/utils/orders';
import { DeliveryUpdateItem, DeliveryUpdateTable } from './DeliveryUpdateTable';
import { OrderDetails, OrderDetailsType } from './OrderDetails';
import { ShipmentTracker, StatusType } from './ShipmentTracker';
import { TextMeUpdates } from './TextMeUpdates';
import { TrackingNumber } from './TrackingNumber';
import * as styles from './ShipmentTracking.module.scss';

const DATE_TEMPLATE_PROGRESS_BAR = 'EEEE, MMM d';
const DATE_TEMPLATE_STEPS = 'MMM d, yyyy h:mm a';
const SHIPMENT_STATUS_MAP = {
  DELIVERED: {
    text: `Delivered`,
    progress: 100,
    icon: 'check',
    progressBarSubText: '',
    addDate: true,
    className: 'delivered',
  },
  AVAILABLE_FOR_PICKUP: {
    text: `Available for Pickup`,
    progress: 100,
    icon: 'check',
    progressBarSubText: '',
    addDate: true,
    className: 'delivered',
  },
  OUT_FOR_DELIVERY: {
    text: 'Out for Delivery',
    progress: 75,
    icon: 'person-carry-box',
    progressBarSubText: 'Estimated Delivery by ',
    addDate: true,
    className: 'outForDelivery',
  },
  PENDING: {
    text: 'Waiting for updates from carrier',
    progress: 50,
    icon: 'clock',
    progressBarSubText: 'Estimated Delivery by ',
    addDate: true,
    className: 'processingShipment',
  },
  IN_TRANSIT: {
    text: 'In Transit',
    progress: 50,
    icon: 'truck',
    progressBarSubText: 'Estimated Delivery by ',
    addDate: true,
    className: 'inTransit',
  },
  SHIPPED: {
    text: 'Shipped',
    progress: 50,
    icon: 'box',
    progressBarSubText: 'Estimated Delivery by ',
    addDate: true,
    className: 'packageShipped',
  },
  INFO_RECEIVED: {
    text: 'Processing Shipment',
    progress: 12,
    icon: 'clock',
    progressBarSubText:
      'It will take approximately 5-15 days to ship. Please check back for updates.',
    addDate: false,
    className: 'processingShipment',
  },
  EXPIRED: {
    text: 'Expired',
    progress: 0,
    icon: 'exclamation',
    progressBarSubText: 'Delivery expired',
    addDate: false,
    className: 'error',
  },
  EXCEPTION: {
    text: 'Exception',
    progress: 0,
    icon: 'exclamation',
    progressBarSubText: 'Estimated Delivery by ',
    addDate: true,
    className: 'error',
  },
  ATTEMPT_FAILED: {
    text: 'Attempt Failed',
    progress: 0,
    icon: 'exclamation',
    progressBarSubText: 'Delivery attempt failed',
    addDate: false,
    className: 'error',
  },
};

export const ERROR_MESSAGES = {
  noShipmentTrackingId: 'No Shipment Tracking specified. Please try selecting from below.',
  errorFetchingShipmentTrackingId:
    'The tracking page you attempted is invalid. Please try selecting from below.',
  errorFetchingCarriers: 'Error fetching carriers',
  errorFetchingOrders: 'Error fetching orders',
};

type Order = {
  orderId: number;
  shipments: {
    products: OrderDetailsType[];
    shipmentDetails: {
      orderShipmentId: number;
    };
  }[];
};

type Tracking = {
  shipmentDetails: {
    carrier?: string;
    carrierTrackingUrl?: string;
    orderShipmentId?: number;
    shipmentDate?: string;
    status?: string;
    trackingNumber?: string;
    warehouse?: string;
  };
  estimatedDeliveryDate?: string;
  orderId: number;
  checkpoints: {
    checkpointTime?: string;
    countryName?: string;
    latitude?: string;
    location?: string;
    longitude?: string;
    message?: string;
    tag?: string;
    zipcode?: string;
  }[];
};

type ShipmentStatus = keyof typeof SHIPMENT_STATUS_MAP;

const ShipmentTracking = () => {
  const { orderShipmentId } = useParams() as { orderShipmentId: string };
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(true);
  const { shipmentTracking: shipmentTrackingFF, chatbotAppliedLab: showChatbot } = useFlags();
  const [tracking, setTracking] = useState<Tracking | undefined>();
  const [isLoadingTracking, setIsLoadingTracking] = useState(true);
  const [carriers, setCarriers] = useState<Record<string, { logoUrl: string }> | undefined>();
  const navigate = useNavigate();
  const orderShipmentIdNumber = parseInt(orderShipmentId);

  const getOrderDetails = (orderId: number | undefined, orderShipmentId: number) => {
    if (!orderId) return null;

    const orderProducts = orders.find((order) => order.orderId === orderId);
    if (!orderProducts) return null;

    const shipment = orderProducts.shipments.find(
      (shipment) => shipment.shipmentDetails.orderShipmentId === orderShipmentId,
    );

    if (!shipment) return null;

    return shipment.products;
  };

  const getStatusesFromTracking = (tracking?: Tracking) => {
    if (!tracking || !tracking?.checkpoints?.length) return null;

    const { estimatedDeliveryDate } = tracking;

    return tracking.checkpoints
      .map((checkpoint) => {
        const status = SHIPMENT_STATUS_MAP[checkpoint.tag as keyof typeof SHIPMENT_STATUS_MAP];
        const progressBarTag: ShipmentStatus = (checkpoint.tag as ShipmentStatus) || 'IN_TRANSIT';
        const { progressBarSubText, addDate } = SHIPMENT_STATUS_MAP[progressBarTag];
        const checkpointDate =
          checkpoint.tag !== 'DELIVERED' && checkpoint.tag !== 'AVAILABLE_FOR_PICKUP'
            ? estimatedDeliveryDate || checkpoint.checkpointTime
            : checkpoint.checkpointTime;
        const formattedDate = addDate
          ? format(checkpointDate as string, DATE_TEMPLATE_PROGRESS_BAR)
          : '';
        const formattedProgressBarSubtext = `${progressBarSubText}${formattedDate}`;

        return {
          description: 'text' in status ? status.text : '',
          location: checkpoint.location ?? '',
          progressBarSubtext: formattedProgressBarSubtext,
          subtext: format(checkpoint.checkpointTime as string, DATE_TEMPLATE_STEPS),
          statusName: status.className,
          percentage: status.progress,
        };
      })
      .reverse();
  };

  useEffect(() => {
    if (!shipmentTrackingFF) {
      navigate('/my-account/orders', { replace: true });
    }
    if (!orderShipmentIdNumber) {
      navigate('/my-account/orders', {
        replace: true,
        state: { error: ERROR_MESSAGES.noShipmentTrackingId },
      });
    }
    if (tracking?.checkpoints.length === 0) {
      const externalTrackingUrl = getTrackingUrl(
        {
          carrier: tracking?.shipmentDetails?.carrier,
          trackingNumber: tracking?.shipmentDetails?.trackingNumber,
        },
        false,
      );
      if (!tracking.estimatedDeliveryDate && externalTrackingUrl) {
        window.location.assign(externalTrackingUrl);
      } else {
        navigate('/my-account/orders', {
          replace: true,
          state: { error: ERROR_MESSAGES.errorFetchingShipmentTrackingId },
        });
      }
    }
  }, [shipmentTrackingFF, navigate, orderShipmentIdNumber, tracking]);

  useEffect(() => {
    const fetchOrderTracking = async () => {
      try {
        const response = await getOrderTracking([orderShipmentIdNumber]);
        if (response.errors.length === 0) {
          setTracking(response.trackings[orderShipmentIdNumber]);
        } else {
          throw new Error(ERROR_MESSAGES.errorFetchingShipmentTrackingId);
        }
      } catch (e) {
        navigate('/my-account/orders', {
          replace: true,
          state: { error: ERROR_MESSAGES.errorFetchingShipmentTrackingId },
        });
        datadogLogs.logger.error(ERROR_MESSAGES.errorFetchingShipmentTrackingId, {}, e as Error);
      } finally {
        setIsLoadingTracking(false);
      }
    };
    fetchOrderTracking();
  }, [orderShipmentIdNumber, navigate]);

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        const response = await getCarriers();
        setCarriers(response);
      } catch (e) {
        datadogLogs.logger.error(ERROR_MESSAGES.errorFetchingCarriers, {}, e as Error);
      } finally {
        setIsLoadingCarriers(false);
      }
    };
    fetchCarriers();
  }, []);

  useEffect(() => {
    trackPageLoad({
      properties: {
        title: 'Shipment Tracking Page',
      },
    });
  }, []);

  useEffect(() => {
    if (!isLoadingTracking) {
      const fetchOrders = async () => {
        try {
          const response = await getOrders();
          setOrders(response);
        } catch (e) {
          datadogLogs.logger.error(ERROR_MESSAGES.errorFetchingOrders, {}, e as Error);
        } finally {
          setIsLoadingOrders(false);
        }
      };
      fetchOrders();
    }
  }, [isLoadingTracking]);

  if (isLoadingTracking || isLoadingOrders || isLoadingCarriers) {
    return null;
  }

  const trackingDetails = {
    carrier: tracking?.shipmentDetails?.carrier,
    carrierLogo: carriers?.[tracking?.shipmentDetails?.carrier as keyof typeof carriers]?.logoUrl,
    trackingNumber: tracking?.shipmentDetails?.trackingNumber,
    carrierTrackingUrl: tracking?.shipmentDetails.carrierTrackingUrl,
  };
  const orderDetails = getOrderDetails(tracking?.orderId, orderShipmentIdNumber);
  const statuses = getStatusesFromTracking(tracking);

  return (
    <Layout hideChatWidget={!showChatbot}>
      <PageContainer>
        <div className={styles.container}>
          <div className={styles.pageContent}>
            <Link to={`/my-account/orders`} className={styles.link}>
              <FontAwesomeIcon icon={faChevronLeft} />{' '}
              <span className={styles.backCopy}>Back to Orders</span>
            </Link>
            <div className={styles.shipmentDetails} data-testid="shipment-tracking-details">
              <h1 className={styles.shipmentDetailsTitle}>Shipment Details</h1>
              {orderDetails && <OrderDetails orderDetails={orderDetails} />}
              {(statuses?.length ?? 0) > 0 && (
                <>
                  <ShipmentTracker status={statuses?.[0] as StatusType} />
                  <DeliveryUpdateTable statuses={statuses as DeliveryUpdateItem[]} />
                </>
              )}
              {trackingDetails && <TrackingNumber trackingDetails={trackingDetails} />}
              <div className={styles.disclaimer}>
                This tracking page contains the latest information/updates about your shipment. For
                members on the East Coast, initial tracking updates may be delayed as carriers often
                don&apos;t scan orders until they reach the main processing hub. All delivery
                updates are provided from the carrier and estimated timelines are not always
                correct.
              </div>
              <div className={styles.feedbackButtonsContainer}>
                <TextMeUpdates />
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default ShipmentTracking;
